import { useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Loader2, AlertCircle, Gift } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatError } from "../lib/api";

export default function RegisterPage() {
  const { register: doReg } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const initialRef = sp.get("ref") || (() => { try { return localStorage.getItem("cf_ref_code") || ""; } catch (e) { return ""; } })();
  const [ref, setRef] = useState(initialRef);
  const [showRef, setShowRef] = useState(!!initialRef);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await doReg(name, email, password, ref);
      toast.success("Account created! 25 free credits.");
      try { localStorage.removeItem("cf_ref_code"); } catch (e) { /* ignore */ }
      nav("/dashboard");
    } catch (x) {
      logger.error("Register failed", x);
      setErr(formatError(x.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="register-page">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="flex items-center gap-2 mb-10 cursor-pointer" onClick={() => nav("/")}>
            <Zap className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span className="font-black text-xl tracking-tighter text-zinc-950 font-heading">CopyForge</span>
          </div>
          <h1 className="text-3xl font-black tracking-tighter text-zinc-950 mb-2 font-heading">Start free today</h1>
          <p className="text-zinc-500 mb-8">25 free credits. No card.</p>
          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-6 flex items-center gap-2" data-testid="register-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {err}
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Name</label>
              <input data-testid="register-name-input" type="text" value={name} onChange={(x) => setName(x.target.value)} required className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="Your name" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Email</label>
              <input data-testid="register-email-input" type="email" value={email} onChange={(x) => setEmail(x.target.value)} required className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Password</label>
              <input data-testid="register-password-input" type="password" value={password} onChange={(x) => setPassword(x.target.value)} required minLength={6} className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="Min 6 characters" />
            </div>
            {showRef ? (
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1">Referral Code</label>
                <input data-testid="register-referral-input" type="text" value={ref} onChange={(x) => setRef(x.target.value.toUpperCase())} className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="XXXXXXXX" maxLength={8} />
                {ref && <p className="text-xs text-green-600 mt-1 font-medium">+5 bonus credits!</p>}
              </div>
            ) : (
              <button type="button" onClick={() => setShowRef(true)} className="text-sm text-brand font-medium hover:underline flex items-center gap-1" data-testid="show-referral-btn">
                <Gift className="w-4 h-4" /> Referral code?
              </button>
            )}
            <button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full bg-brand text-white font-semibold py-3 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
            </button>
            <p className="text-xs text-zinc-400 text-center">
              By creating an account, you agree to our{" "}
              <button type="button" onClick={() => nav("/terms")} className="text-brand hover:underline">Terms</button>{" "}and{" "}
              <button type="button" onClick={() => nav("/privacy")} className="text-brand hover:underline">Privacy Policy</button>.
            </p>
          </form>
          <p className="text-sm text-zinc-500 mt-6 text-center">
            Have an account? <button data-testid="go-to-login-link" onClick={() => nav("/login")} className="text-brand font-semibold hover:underline">Sign in</button>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 bg-zinc-950 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,47,167,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,47,167,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-md relative z-10">
          <Zap className="w-12 h-12 text-brand mb-6" />
          <h2 className="text-3xl font-black tracking-tighter text-white mb-4 font-heading">25 free credits.<br />Zero commitment.</h2>
          <p className="text-zinc-400 leading-relaxed mb-8">Generate in under 30 seconds.</p>
        </div>
      </div>
    </div>
  );
}
